import {combineReducers, configureStore as configureToolkitStore, createListenerMiddleware} from "@reduxjs/toolkit";
import {OtsAsyncThunkConfigExtra, otsMiddleware, otsReducers} from "@ots-frontend/ots-data";
import {Action} from "redux";
import {ThunkDispatch} from "redux-thunk";
import {logout} from "./auth/actions";
import {authReducer} from "./auth/auth-store";
import {uiReducer} from "./ui/ui-store";
import {mapReducer} from "./map/map-store";
import {
  CONTAINER_STATUS_PAGE_WIDTHS_KEY,
  containerStatusPageReducer
} from "./container-status-page/container-status-page-store";
import {otsUiReducers} from "@ots-frontend/ots-ui";
import {ticketPageReducer} from "./ticket-page/ticket-page-store";
import {setContainerStatusPageStateWidths} from "./container-status-page/actions";
import {DISPATCH_PAGE_WIDTHS_KEY, dispatchPageReducer} from "./dispatch-page/dispatch-page-store";
import {setTicketPageStateOilWidths, setTicketPageStateWidths} from "./ticket-page/actions";
import {setDispatchPageStateWidths} from "./dispatch-page/actions";
import {dispatchRequestPageReducer} from "./dispatch-request-page/dispatch-request-page-store";
import {setDispatchRequestPageStateWidths} from "./dispatch-request-page/actions";
import {driverActivityDayPageReducer} from "./driver-activity-day-page/driver-activity-page-store";
import {shiftPageReducer} from "./shift-page/shift-page-store";
import {setShiftPageStateWidths} from "./shift-page/actions";

export type AppAsyncThunkConfigExtra = OtsAsyncThunkConfigExtra & {
  // extra props here
};

export type AppThunkDispatch = ThunkDispatch<
  AppState,
  AppAsyncThunkConfigExtra,
  Action<string>
>;

const reducers = {
  ...otsReducers,
  ...otsUiReducers,
  auth: authReducer,
  ui: uiReducer,
  map: mapReducer,
  containerStatusPage: containerStatusPageReducer,
  dispatchPage: dispatchPageReducer,
  dispatchRequestPage: dispatchRequestPageReducer,
  driverActivityDayPage: driverActivityDayPageReducer,
  shiftPage: shiftPageReducer,
  ticketPage: ticketPageReducer,
};

const appReducer = combineReducers(reducers);

export type AppState = ReturnType<typeof appReducer>;

const rootReducer = (state: any, action: any) => {
  switch (action.type) {
    // clear out the application state when use logs out.
    case logout.type:
      state = undefined;
      break;
  }

  return appReducer(state, action);
};

export const storeExtra: OtsAsyncThunkConfigExtra = {
  clientApp: "dispatchgo-web",
  tenantCode: import.meta.env.VITE_TENANT_CODE,
  baseUrl: import.meta.env.VITE_BASE_API_URL,
  getAuthToken: (state: AppState): string | undefined => {
    return state.auth.authToken;
  },
  getRegionId: (state: AppState): string | undefined => {
    return state.entities.regions.currentRegionId;
  }
}

export const prefsMiddleware = createListenerMiddleware<AppState>()
prefsMiddleware.startListening({
  actionCreator: setContainerStatusPageStateWidths,
  effect: (action, api) => {
    localStorage.setItem(CONTAINER_STATUS_PAGE_WIDTHS_KEY, JSON.stringify(action.payload))
  }
})
prefsMiddleware.startListening({
  actionCreator: setTicketPageStateWidths,
  effect: (action, api) => {
    localStorage.setItem("ticketPageWidths3", JSON.stringify(action.payload))
  }
})
prefsMiddleware.startListening({
  actionCreator: setTicketPageStateOilWidths,
  effect: (action, api) => {
    localStorage.setItem("ticketPageOilWidths3", JSON.stringify(action.payload))
  }
})
prefsMiddleware.startListening({
  actionCreator: setDispatchPageStateWidths,
  effect: (action, api) => {
    localStorage.setItem(DISPATCH_PAGE_WIDTHS_KEY, JSON.stringify(action.payload))
  }
})
prefsMiddleware.startListening({
  actionCreator: setDispatchRequestPageStateWidths,
  effect: (action, api) => {
    localStorage.setItem("dispatchRequestPageWidths2", JSON.stringify(action.payload))
  }
})
prefsMiddleware.startListening({
  actionCreator: setShiftPageStateWidths,
  effect: (action, api) => {
    localStorage.setItem("shiftPageWidths1", JSON.stringify(action.payload))
  }
})

/**
 * Configure store for development environment.
 */
const configureDevStore = () => {
  return configureToolkitStore({
    reducer: rootReducer,
    devTools: true,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        thunk: {
          extraArgument: storeExtra,
        },
        immutableCheck: false,
        serializableCheck: false
      })
        .concat(...otsMiddleware)
        .prepend(prefsMiddleware.middleware)
  });
};

/**
 * Configure store for production environment.
 */
const configureProdStore = () => {
  return configureToolkitStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        thunk: {
          extraArgument: storeExtra
        }
      })
        .concat(...otsMiddleware)
        .prepend(prefsMiddleware.middleware)
  });
};

const configureStore = () => {
  if (import.meta.env.PROD) {
    return configureProdStore();
  } else {
    return configureDevStore();
  }
};

export const store = configureStore();

export type AppDispatch = typeof store.dispatch;
